import { useEffect, useRef, useState } from "react"
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fab,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { useSnackbar } from "../../../utils/notificacoes"
import {
    executarAcoesCardapioIA,
    interpretarCardapioIA,
    transcreverAudioCardapioIA,
} from "../../../services/cardapio"
import { useCardapioContext } from "./CardapioContext"

const useStyles = makeStyles(theme => ({
    fab: {
        position: "fixed",
        bottom: theme.spacing(4),
        right: theme.spacing(4),
        zIndex: 1300,
        background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
        color: "white",
        fontWeight: "bold",
        fontSize: 13,
        boxShadow: "0 4px 20px rgba(124,58,237,0.5)",
        // Arrastável: evita que o gesto vire scroll no touch e sinaliza o "pegar".
        cursor: "grab",
        touchAction: "none",
        "&:active": {
            cursor: "grabbing",
        },
        "&:hover": {
            background: "linear-gradient(135deg, #5b21b6 0%, #3730a3 100%)",
            transform: "scale(1.05)",
            boxShadow: "0 6px 24px rgba(124,58,237,0.6)",
        },
        transition: "box-shadow 0.2s ease, background 0.2s ease",
    },
    inlineBtn: {
        borderRadius: 25,
        textTransform: "none",
        fontWeight: 600,
        padding: "8px 22px",
        transition: "all 0.2s ease",
        color: "white",
        background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
        boxShadow: "0 4px 15px rgba(124,58,237,0.35)",
        "&:hover": {
            background: "linear-gradient(135deg, #5b21b6 0%, #3730a3 100%)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(124,58,237,0.45)",
        },
    },
    dialogTitle: {
        background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
        color: "white",
        paddingBottom: theme.spacing(2),
    },
    resumoBox: {
        backgroundColor: "#f5f3ff",
        border: "1px solid #ddd6fe",
        borderRadius: 8,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    badge: {
        display: "inline-block",
        backgroundColor: "#7c3aed",
        color: "white",
        borderRadius: 12,
        padding: "3px 12px",
        fontSize: 13,
        fontWeight: "bold",
        marginBottom: theme.spacing(1),
    },
    listaAlteracoes: {
        maxHeight: 320,
        overflowY: "auto",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        marginTop: theme.spacing(1),
    },
    itemAlteracao: {
        padding: "8px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        "&:not(:last-child)": {
            borderBottom: "1px solid #f3f4f6",
        },
    },
    breadcrumb: {
        fontSize: 11,
        color: "#6b7280",
        fontStyle: "italic",
    },
    nomeItem: {
        fontSize: 13,
        fontWeight: 600,
        color: "#111827",
    },
    acaoItem: {
        fontSize: 12,
        color: "#4b5563",
    },
    tipoChip: {
        display: "inline-block",
        fontSize: 10,
        fontWeight: 700,
        borderRadius: 4,
        padding: "1px 6px",
        marginRight: 6,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    botaoSalvar: {
        backgroundColor: "#14A44D",
        color: "white",
        "&:hover": { backgroundColor: "#108a3e" },
    },
    botaoDescartar: {
        color: "#dc2626",
        borderColor: "#dc2626",
        "&:hover": { backgroundColor: "#fef2f2" },
    },
    hint: {
        color: theme.palette.text.secondary,
        fontSize: 12,
        marginBottom: theme.spacing(2),
    },
    avisoCardapioGrande: {
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        backgroundColor: "#fffbeb",
        border: "1px solid #fde68a",
        borderRadius: 8,
        padding: "8px 12px",
        marginBottom: theme.spacing(2),
        fontSize: 12,
        color: "#92400e",
    },
    "@keyframes iaFade": {
        "0%": { opacity: 0, transform: "translateY(6px)" },
        "100%": { opacity: 1, transform: "translateY(0)" },
    },
    "@keyframes iaPulse": {
        "0%, 100%": { transform: "scale(1)", opacity: 0.85 },
        "50%": { transform: "scale(1.18)", opacity: 1 },
    },
    "@keyframes iaBarra": {
        "0%": { left: "-40%" },
        "100%": { left: "100%" },
    },
    progresso: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(2),
        padding: theme.spacing(5, 3),
        textAlign: "center",
        minHeight: 210,
    },
    progressoSparkle: {
        fontSize: 42,
        lineHeight: 1,
        background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        animation: "$iaPulse 1.6s ease-in-out infinite",
    },
    progressoMensagem: {
        fontSize: 16,
        fontWeight: 600,
        color: "#4f46e5",
        minHeight: 24,
        animation: "$iaFade 0.45s ease",
    },
    progressoBarra: {
        width: "100%",
        maxWidth: 280,
        height: 4,
        borderRadius: 4,
        backgroundColor: "#ede9fe",
        overflow: "hidden",
        position: "relative",
        "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            bottom: 0,
            width: "40%",
            borderRadius: 4,
            background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
            animation: "$iaBarra 1.4s ease-in-out infinite",
        },
    },
    progressoSub: {
        fontSize: 12,
        color: "#9ca3af",
        maxWidth: 320,
    },
    micRow: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginTop: 10,
        flexWrap: "wrap",
    },
    botaoFalar: {
        textTransform: "none",
        borderRadius: 20,
        border: "1px solid #ddd6fe",
        color: "#5b21b6",
        fontWeight: 600,
        "&:hover": { backgroundColor: "#f5f3ff" },
    },
    botaoParar: {
        textTransform: "none",
        borderRadius: 20,
        fontWeight: 600,
        backgroundColor: "#fee2e2",
        color: "#b91c1c",
        border: "1px solid #fecaca",
        "&:hover": { backgroundColor: "#fecaca" },
    },
    pontoGravando: {
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: "#dc2626",
        display: "inline-block",
        animation: "$iaPulse 1s ease-in-out infinite",
    },
}))

// Mensagens que "piscam" durante cada fase longa da IA, para o usuário saber que algo
// está acontecendo (a chamada ao backend pode levar dezenas de segundos). Avançam sozinhas
// e param na última — o spinner/barra seguem animando, sinalizando que ainda está em curso.
const MSGS_ANALISE = [
    "Entendendo a sua instrução…",
    "Analisando o cardápio…",
    "Localizando os itens citados…",
    "Montando as alterações…",
    "Revisando os detalhes…",
    "Quase lá…",
]
const MSGS_APLICACAO = [
    "Aplicando as alterações…",
    "Atualizando os itens do cardápio…",
    "Salvando as mudanças…",
    "Quase pronto…",
]

// Painel de progresso indeterminado: cicla `mensagens` (troca a cada ~2,2s, com fade) e
// mantém a última enquanto a operação não termina. A barra e o ícone pulsando garantem
// que nunca pareça travado, mesmo que a resposta demore mais que a lista de mensagens.
function ProgressoIA({ mensagens, sub }) {
    const classes = useStyles()
    const [i, setI] = useState(0)
    useEffect(() => {
        setI(0)
        const id = setInterval(() => {
            setI(prev => (prev < mensagens.length - 1 ? prev + 1 : prev))
        }, 2200)
        return () => clearInterval(id)
    }, [mensagens])
    return (
        <div className={classes.progresso}>
            <span className={classes.progressoSparkle}>✦</span>
            {/* key={i} força o remount → reexecuta a animação de fade a cada troca. */}
            <Typography key={i} className={classes.progressoMensagem}>
                {mensagens[i]}
            </Typography>
            <div className={classes.progressoBarra} />
            {sub ? <Typography className={classes.progressoSub}>{sub}</Typography> : null}
        </div>
    )
}

// Acima disso, mandar o comando sem citar categoria fica lento e caro: o backend precisa
// enviar o cardapio inteiro para a IA em vez de so a fatia relevante.
const PRODUTOS_CARDAPIO_GRANDE = 200

const TIPO_CORES = {
    categoria: { bg: "#ede9fe", color: "#5b21b6", label: "Categoria" },
    produto: { bg: "#e0f2fe", color: "#0369a1", label: "Produto" },
    opcao: { bg: "#fef9c3", color: "#854d0e", label: "Opção" },
}

function acharCategoria(categorias, id, nome) {
    const nl = (nome || "").toLowerCase()
    return (categorias || []).find(c =>
        id != null ? c.id === id : nl && c.nome?.toLowerCase().includes(nl)
    )
}

function acharProduto(categorias, id, nome) {
    const nl = (nome || "").toLowerCase()
    for (const c of categorias || [])
        for (const p of c.produtos || [])
            if (id != null ? p.id === id : nl && p.nome?.toLowerCase().includes(nl))
                return { produto: p, categoria: c }
    return null
}

function acharOpcao(categorias, id, nome) {
    const nl = (nome || "").toLowerCase()
    for (const c of categorias || [])
        for (const p of c.produtos || [])
            for (const g of p.opcoes || [])
                for (const o of g.opcoes || [])
                    if (id != null ? o.id === id : nl && o.nome?.toLowerCase().includes(nl))
                        return { opcao: o, produto: p, categoria: c }
    return null
}

function acharGrupo(categorias, id, nome) {
    const nl = (nome || "").toLowerCase()
    for (const c of categorias || [])
        for (const p of c.produtos || [])
            for (const g of p.opcoes || [])
                if (id != null ? g.id === id : nl && g.nome?.toLowerCase().includes(nl))
                    return { grupo: g, produto: p, categoria: c }
    return null
}

const ROTULOS_CAMPOS = {
    nome: "Nome",
    valor: "Preço",
    codigo: "Código PDV",
    ordemExib: "Ordem",
    descricao: "Descrição",
    ativo: "Disponível",
    permiteObservacao: "Permite observação",
    tipoProducao: "CFOP",
    impressaoAlternada: "Impressora",
    produtoExibicao: "Produto de exibição",
    promocional: "Promocional",
    apenasPrimeiraCompra: "Só 1ª compra",
    pontuarProgramaFidelidade: "Pontua fidelidade",
    pontos: "Pontos",
    quantidadeMinima: "Qtd mínima",
    quantidadeMaxima: "Qtd máxima",
    diaSemana: "Dia",
    turnoProduto: "Turno",
    exibicaoCategoriaEnum: "Exibição",
    modalidadePedido: "Modalidade",
    adicional: "Tipo do grupo",
    considerarMaiorValor: "Considerar maior valor",
    considerarMedia: "Considerar média",
    informativo: "Apenas informativo",
}

function descreverCampos(campos) {
    if (!campos || typeof campos !== "object") return ["Alterar campos"]
    return Object.keys(campos).map(k => {
        const v = campos[k]
        const rot = ROTULOS_CAMPOS[k] || k
        if (typeof v === "boolean") return `${rot}: ${v ? "Sim" : "Não"}`
        if (v === null || v === "") return `${rot}: (vazio)`
        if (k === "valor") return `${rot}: R$ ${Number(v).toFixed(2)}`
        return `${rot}: ${v}`
    })
}

// Converte a lista de acoes da IA em itens legiveis para a previa (antes de aplicar).
function descreverAcoes(acoes, categorias) {
    const out = []
    const preco = v => `R$ ${Number(v || 0).toFixed(2)}`
    for (const a of Array.isArray(acoes) ? acoes : []) {
        const { tipo, id, nome, novoValor: nv } = a
        switch (tipo) {
            case "INATIVAR_PRODUTO":
            case "ATIVAR_PRODUTO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome || "produto",
                    breadcrumb: r?.categoria?.nome,
                    acoes: [tipo === "ATIVAR_PRODUTO" ? "Ativar" : "Inativar"],
                })
                break
            }
            case "INATIVAR_CATEGORIA":
            case "ATIVAR_CATEGORIA": {
                const c = acharCategoria(categorias, id, nome)
                out.push({
                    tipo: "categoria",
                    nome: c?.nome || nome || "categoria",
                    acoes: [tipo === "ATIVAR_CATEGORIA" ? "Ativar" : "Inativar"],
                })
                break
            }
            case "ALTERAR_NOME_CATEGORIA": {
                const c = acharCategoria(categorias, id, nome)
                out.push({ tipo: "categoria", nome: c?.nome || nome, acoes: [`Nome -> "${nv}"`] })
                break
            }
            case "INATIVAR_TODOS_PRODUTOS_CATEGORIA":
            case "ATIVAR_TODOS_PRODUTOS_CATEGORIA": {
                const c = acharCategoria(categorias, id, nome)
                const at = tipo.startsWith("ATIVAR")
                out.push({
                    tipo: "categoria",
                    nome: c?.nome || nome,
                    acoes: [`${at ? "Ativar" : "Inativar"} todos os produtos`],
                })
                break
            }
            case "INATIVAR_TODOS_ADICIONAIS":
            case "ATIVAR_TODOS_ADICIONAIS": {
                const at = tipo.startsWith("ATIVAR")
                out.push({
                    tipo: "opcao",
                    nome: "Todos os adicionais",
                    acoes: [`${at ? "Ativar" : "Inativar"} todas as opcoes`],
                })
                break
            }
            case "ALTERAR_PRECO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome,
                    breadcrumb: r?.categoria?.nome,
                    acoes: [`Preco -> ${preco(a.valor)}`],
                })
                break
            }
            case "ALTERAR_NOME_PRODUTO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome,
                    breadcrumb: r?.categoria?.nome,
                    acoes: [`Nome -> "${nv}"`],
                })
                break
            }
            case "ALTERAR_DESCRICAO_PRODUTO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome,
                    breadcrumb: r?.categoria?.nome,
                    acoes: ["Descricao alterada"],
                })
                break
            }
            case "ALTERAR_CODIGO_PRODUTO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome,
                    breadcrumb: r?.categoria?.nome,
                    acoes: [`Codigo PDV -> "${nv}"`],
                })
                break
            }
            case "INATIVAR_OPCAO":
            case "ATIVAR_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({
                    tipo: "opcao",
                    nome: r?.opcao?.nome || nome,
                    breadcrumb: r ? `${r.categoria.nome} > ${r.produto.nome}` : undefined,
                    acoes: [tipo === "ATIVAR_OPCAO" ? "Ativar" : "Inativar"],
                })
                break
            }
            case "ALTERAR_NOME_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({ tipo: "opcao", nome: r?.opcao?.nome || nome, acoes: [`Nome -> "${nv}"`] })
                break
            }
            case "ALTERAR_CODIGO_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({ tipo: "opcao", nome: r?.opcao?.nome || nome, acoes: [`Codigo PDV -> "${nv}"`] })
                break
            }
            case "INCLUIR_PRODUTO": {
                out.push({
                    tipo: "produto",
                    nome: nome || "Novo produto",
                    breadcrumb: a.categoria,
                    acoes: [`Criar produto (${preco(a.valor)})`],
                })
                break
            }
            case "INCLUIR_GRUPO_OPCOES": {
                const r = acharProduto(categorias, id, nome)
                const ops = (a.opcoes || []).map(o => `${o.nome} (${preco(o.valor)})`)
                out.push({
                    tipo: "opcao",
                    nome: nv || "Novo passo de montagem",
                    breadcrumb: r?.produto?.nome
                        ? `Produto: ${r.produto.nome}`
                        : nome
                        ? `Produto: ${nome}`
                        : undefined,
                    acoes: [
                        "Criar passo de montagem",
                        ...(ops.length ? [`Opcoes: ${ops.join(", ")}`] : []),
                    ],
                })
                break
            }
            case "INCLUIR_CATEGORIA": {
                out.push({
                    tipo: "categoria",
                    nome: nome || "Nova categoria",
                    acoes: ["Criar categoria"],
                })
                break
            }
            case "INCLUIR_OPCAO": {
                const r = acharGrupo(categorias, id, nome)
                out.push({
                    tipo: "opcao",
                    nome: nv || "Nova opção",
                    breadcrumb: r?.grupo?.nome
                        ? `Passo: ${r.grupo.nome}`
                        : nome
                        ? `Passo: ${nome}`
                        : undefined,
                    acoes: [`Adicionar ao passo (${preco(a.valor)})`],
                })
                break
            }
            case "INATIVAR_GRUPO":
            case "ATIVAR_GRUPO": {
                const r = acharGrupo(categorias, id, nome)
                out.push({
                    tipo: "opcao",
                    nome: r?.grupo?.nome || nome || "passo",
                    breadcrumb: r?.produto?.nome ? `Produto: ${r.produto.nome}` : undefined,
                    acoes: [tipo === "ATIVAR_GRUPO" ? "Ativar passo" : "Inativar passo"],
                })
                break
            }
            case "ALTERAR_NOME_GRUPO": {
                const r = acharGrupo(categorias, id, nome)
                out.push({ tipo: "opcao", nome: r?.grupo?.nome || nome, acoes: [`Nome do passo -> "${nv}"`] })
                break
            }
            case "ALTERAR_QUANTIDADES_GRUPO": {
                const r = acharGrupo(categorias, id, nome)
                const mn = a.quantidadeMinima != null ? a.quantidadeMinima : "?"
                const mx = a.quantidadeMaxima != null ? a.quantidadeMaxima : "?"
                out.push({ tipo: "opcao", nome: r?.grupo?.nome || nome, acoes: [`Mín/Máx -> ${mn}/${mx}`] })
                break
            }
            case "ALTERAR_ORDEM_GRUPO": {
                const r = acharGrupo(categorias, id, nome)
                out.push({ tipo: "opcao", nome: r?.grupo?.nome || nome, acoes: [`Ordem -> ${a.valor}`] })
                break
            }
            case "ALTERAR_ORDEM_CATEGORIA": {
                const c = acharCategoria(categorias, id, nome)
                out.push({ tipo: "categoria", nome: c?.nome || nome, acoes: [`Ordem -> ${a.valor}`] })
                break
            }
            case "ALTERAR_ORDEM_PRODUTO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome,
                    breadcrumb: r?.categoria?.nome,
                    acoes: [`Ordem -> ${a.valor}`],
                })
                break
            }
            case "ALTERAR_ORDEM_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({ tipo: "opcao", nome: r?.opcao?.nome || nome, acoes: [`Ordem -> ${a.valor}`] })
                break
            }
            case "ALTERAR_PRECO_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({
                    tipo: "opcao",
                    nome: r?.opcao?.nome || nome,
                    breadcrumb: r ? `${r.categoria.nome} > ${r.produto.nome}` : undefined,
                    acoes: [`Preço -> ${preco(a.valor)}`],
                })
                break
            }
            case "ALTERAR_DESCRICAO_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({ tipo: "opcao", nome: r?.opcao?.nome || nome, acoes: ["Descrição alterada"] })
                break
            }
            case "ALTERAR_CATEGORIA": {
                const c = acharCategoria(categorias, id, nome)
                out.push({ tipo: "categoria", nome: c?.nome || nome, acoes: descreverCampos(a.campos) })
                break
            }
            case "ALTERAR_PRODUTO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome,
                    breadcrumb: r?.categoria?.nome,
                    acoes: descreverCampos(a.campos),
                })
                break
            }
            case "ALTERAR_GRUPO": {
                const r = acharGrupo(categorias, id, nome)
                out.push({
                    tipo: "opcao",
                    nome: r?.grupo?.nome || nome,
                    breadcrumb: r?.produto?.nome ? `Produto: ${r.produto.nome}` : undefined,
                    acoes: descreverCampos(a.campos),
                })
                break
            }
            case "ALTERAR_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({
                    tipo: "opcao",
                    nome: r?.opcao?.nome || nome,
                    breadcrumb: r ? `${r.categoria.nome} > ${r.produto.nome}` : undefined,
                    acoes: descreverCampos(a.campos),
                })
                break
            }
            default:
                out.push({ tipo: "produto", nome: nome || tipo || "acao", acoes: [tipo || "Acao"] })
        }
    }
    return out
}

export default function FloatingIAButton({ inline = false, style }) {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()

    const { categorias, recarregarCardapio } = useCardapioContext()

    const [open, setOpen] = useState(false)
    const [texto, setTexto] = useState("")
    const [loadingIA, setLoadingIA] = useState(false)
    const [salvando, setSalvando] = useState(false)
    const [resultado, setResultado] = useState(null)

    // Comando de voz: grava no navegador (MediaRecorder) e manda o áudio para o backend
    // transcrever com Whisper; o texto volta para o campo, o usuário revisa e segue o fluxo
    // normal. `gravando` = capturando o microfone; `transcrevendo` = aguardando o Whisper.
    const [gravando, setGravando] = useState(false)
    const [transcrevendo, setTranscrevendo] = useState(false)
    // Diálogo guiado quando a permissão do microfone está bloqueada: o navegador não deixa a
    // página reabrir o popup nativo depois de negado, então explicamos onde reabilitar.
    const [dialogPermissao, setDialogPermissao] = useState(false)
    const mediaRecorderRef = useRef(null)
    const chunksAudioRef = useRef([])
    const streamAudioRef = useRef(null)

    // Posição do FAB quando arrastado. null = usa o canto padrão (bottom/right do CSS).
    // Persistida para o botão continuar onde o usuário deixou entre recarregamentos.
    const [posicao, setPosicao] = useState(() => {
        try {
            const salvo = localStorage.getItem("posicaoBotaoIA")
            return salvo ? JSON.parse(salvo) : null
        } catch {
            return null
        }
    })
    // Estado do gesto mantido em ref para não re-renderizar a cada movimento do mouse.
    const arrasto = useRef({ ativo: false, moveu: false, sx: 0, sy: 0, dx: 0, dy: 0, w: 0, h: 0 })

    const handlePointerDown = e => {
        const r = e.currentTarget.getBoundingClientRect()
        arrasto.current = {
            ativo: true,
            moveu: false,
            sx: e.clientX,
            sy: e.clientY,
            dx: e.clientX - r.left,
            dy: e.clientY - r.top,
            w: r.width,
            h: r.height,
        }
        e.currentTarget.setPointerCapture?.(e.pointerId)
    }

    const handlePointerMove = e => {
        const a = arrasto.current
        if (!a.ativo) return
        // Só entra em modo "arrastando" depois de passar de um limiar — assim um
        // clique com micro-tremor ainda abre o diálogo em vez de mover o botão.
        if (!a.moveu && Math.hypot(e.clientX - a.sx, e.clientY - a.sy) < 5) return
        a.moveu = true
        const margem = 8
        const x = Math.max(margem, Math.min(e.clientX - a.dx, window.innerWidth - a.w - margem))
        const y = Math.max(margem, Math.min(e.clientY - a.dy, window.innerHeight - a.h - margem))
        setPosicao({ x, y })
    }

    const handlePointerUp = e => {
        const a = arrasto.current
        a.ativo = false
        e.currentTarget.releasePointerCapture?.(e.pointerId)
        if (a.moveu) {
            setPosicao(p => {
                if (p) {
                    try {
                        localStorage.setItem("posicaoBotaoIA", JSON.stringify(p))
                    } catch {
                        /* ignora indisponibilidade do storage */
                    }
                }
                return p
            })
        }
    }

    // Só abre o diálogo se foi um clique de verdade (não o fim de um arrasto).
    const handleClickIA = () => {
        if (arrasto.current.moveu) {
            arrasto.current.moveu = false
            return
        }
        setTexto("")
        setResultado(null)
        setOpen(true)
    }

    const autorizacao = localStorage.getItem("chaveSistema")

    // O cardápio já está em memória, então dá para orientar antes de o usuário digitar —
    // em vez de deixar ele escrever o comando e só então descobrir que ficou lento.
    const totalProdutos = (categorias || []).reduce((n, c) => n + (c.produtos?.length || 0), 0)
    const cardapioGrande = totalProdutos > PRODUTOS_CARDAPIO_GRANDE

    // FASE 1: interpreta o comando e apenas MOSTRA a prévia — nada é aplicado na
    // tela nem persistido até o usuário confirmar no botão "Salvar".
    const handleEnviar = async () => {
        if (!texto.trim()) return
        setLoadingIA(true)
        try {
            const response = await interpretarCardapioIA(texto.trim(), autorizacao)

            if (response.retornoErro) {
                enqueueSnackbar(response.mensagem, { variant: "error" })
                return
            }
            if (response.erro) {
                enqueueSnackbar(response.erro, { variant: "error" })
                return
            }

            const acoes = Array.isArray(response.acoes) ? response.acoes : []
            const alteracoes = descreverAcoes(acoes, categorias)
            setResultado({ acoes, resumo: response.resumo, alteracoes })
        } catch {
            enqueueSnackbar("Erro ao processar a solicitação", { variant: "error" })
        } finally {
            setLoadingIA(false)
        }
    }

    // ── Comando de voz ────────────────────────────────────────────────────────────
    // Escolhe um container que o MediaRecorder suporte E que o Whisper aceite (webm/ogg).
    const escolherMimeAudio = () => {
        if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return ""
        const candidatos = [
            "audio/webm;codecs=opus",
            "audio/webm",
            "audio/ogg;codecs=opus",
            "audio/ogg",
            "audio/mp4",
        ]
        return candidatos.find(t => MediaRecorder.isTypeSupported(t)) || ""
    }

    const pararStreamAudio = () => {
        try {
            streamAudioRef.current?.getTracks().forEach(t => t.stop())
        } catch {
            /* ignore */
        }
        streamAudioRef.current = null
    }

    const iniciarGravacao = async () => {
        if (gravando || transcrevendo || loadingIA) return
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
            enqueueSnackbar("Seu navegador não suporta gravação de áudio.", { variant: "error" })
            return
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            streamAudioRef.current = stream
            const mime = escolherMimeAudio()
            const rec = mime
                ? new MediaRecorder(stream, { mimeType: mime })
                : new MediaRecorder(stream)
            mediaRecorderRef.current = rec
            chunksAudioRef.current = []
            rec.ondataavailable = e => {
                if (e.data && e.data.size > 0) chunksAudioRef.current.push(e.data)
            }
            rec.onstop = () => finalizarGravacao(rec.mimeType || mime || "audio/webm")
            rec.start()
            setGravando(true)
        } catch (e) {
            pararStreamAudio()
            // Mapeia o motivo real do getUserMedia para uma orientação específica. Permissão
            // negada NÃO pode ser reaberta pela página (regra do navegador) — abrimos o
            // diálogo guiado explicando onde reabilitar; os demais casos viram aviso direto.
            const nome = e?.name || ""
            if (nome === "NotAllowedError" || nome === "SecurityError") {
                setDialogPermissao(true)
            } else if (nome === "NotFoundError" || nome === "DevicesNotFoundError") {
                enqueueSnackbar("Nenhum microfone encontrado neste dispositivo.", {
                    variant: "error",
                })
            } else if (nome === "NotReadableError" || nome === "TrackStartError") {
                enqueueSnackbar("O microfone está em uso por outro aplicativo.", {
                    variant: "error",
                })
            } else {
                enqueueSnackbar(
                    "Não foi possível acessar o microfone. Verifique a permissão do navegador.",
                    { variant: "error" }
                )
            }
        }
    }

    const pararGravacao = () => {
        try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive")
                mediaRecorderRef.current.stop()
        } catch {
            /* ignore */
        }
        setGravando(false)
    }

    // Ao parar: monta o blob, converte para base64 (data URL) e manda transcrever. O texto
    // volta para o campo (anexa ao que já houver) para o usuário revisar antes de enviar.
    const finalizarGravacao = async mimeType => {
        pararStreamAudio()
        const chunks = chunksAudioRef.current || []
        chunksAudioRef.current = []
        if (!chunks.length) return
        const blob = new Blob(chunks, { type: mimeType })
        setTranscrevendo(true)
        try {
            const dataUrl = await new Promise((resolve, reject) => {
                const fr = new FileReader()
                fr.onload = () => resolve(fr.result)
                fr.onerror = reject
                fr.readAsDataURL(blob)
            })
            const resp = await transcreverAudioCardapioIA(dataUrl, mimeType, autorizacao)
            if (resp.retornoErro) {
                enqueueSnackbar(resp.mensagem, { variant: "error" })
                return
            }
            if (resp.erro) {
                enqueueSnackbar(resp.erro, { variant: "error" })
                return
            }
            const t = (resp.texto || "").trim()
            if (!t) {
                enqueueSnackbar("Não entendi o áudio. Tente falar de novo, mais devagar.", {
                    variant: "warning",
                })
                return
            }
            setTexto(prev => (prev && prev.trim() ? `${prev.trim()} ${t}` : t))
        } catch {
            enqueueSnackbar("Erro ao transcrever o áudio", { variant: "error" })
        } finally {
            setTranscrevendo(false)
        }
    }

    // Libera o microfone se o componente desmontar no meio de uma gravação.
    useEffect(() => {
        return () => pararStreamAudio()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // FASE 2: só aqui as alterações são persistidas e, em caso de sucesso,
    // refletidas na tela.
    const handleSalvarAgora = async () => {
        if (!resultado?.acoes || resultado.acoes.length === 0) {
            handleFechar()
            return
        }
        setSalvando(true)
        try {
            const response = await executarAcoesCardapioIA(resultado.acoes, autorizacao)
            if (response.retornoErro) {
                enqueueSnackbar(response.mensagem, { variant: "error" })
            } else if (response.erro) {
                enqueueSnackbar(response.erro, { variant: "error" })
            } else {
                // Re-busca pela mesma fonte do F5 (formato correto) em vez de usar
                // o CardapioView retornado, que tem shape diferente e nao renderiza.
                await recarregarCardapio()
                const n = response.alteracoesRealizadas ?? 0
                enqueueSnackbar(
                    n > 0
                        ? `${n} ${n === 1 ? "alteração aplicada" : "alterações aplicadas"} pela IA!`
                        : "Nenhuma alteração foi aplicada.",
                    { variant: n > 0 ? "success" : "warning" }
                )
                handleFechar()
            }
        } catch {
            enqueueSnackbar("Erro ao aplicar as alterações", { variant: "error" })
        } finally {
            setSalvando(false)
        }
    }

    // Descarta a prévia. Como nada foi aplicado à tela, basta fechar.
    const handleDescartar = () => {
        handleFechar()
        enqueueSnackbar("Alterações da IA descartadas", { variant: "success" })
    }

    const handleFechar = () => {
        // Encerra qualquer gravação em curso e libera o microfone antes de fechar.
        pararGravacao()
        pararStreamAudio()
        setOpen(false)
        setTexto("")
        setResultado(null)
    }

    return (
        <>
            {inline ? (
                <Button
                    className={classes.inlineBtn}
                    style={style}
                    onClick={() => {
                        setTexto("")
                        setResultado(null)
                        setOpen(true)
                    }}
                >
                    ✦ IA
                </Button>
            ) : (
                <Tooltip
                    title="Arraste para mover · clique para editar o cardápio com IA"
                    placement="left"
                >
                    <Fab
                        className={classes.fab}
                        variant="extended"
                        style={
                            posicao
                                ? { left: posicao.x, top: posicao.y, right: "auto", bottom: "auto" }
                                : undefined
                        }
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onClick={handleClickIA}
                    >
                        ✦ IA
                    </Fab>
                </Tooltip>
            )}

            <Dialog
                open={open}
                onClose={resultado || loadingIA || transcrevendo ? undefined : handleFechar}
                maxWidth={resultado ? "md" : "sm"}
                fullWidth
                disableBackdropClick={loadingIA || transcrevendo || !!resultado}
            >
                {!resultado ? (
                    <>
                        <DialogTitle className={classes.dialogTitle}>
                            {loadingIA
                                ? "Analisando seu pedido…"
                                : transcrevendo
                                ? "Transcrevendo o áudio…"
                                : "Editar cardápio com Inteligência Artificial"}
                        </DialogTitle>
                        <DialogContent style={{ paddingTop: 20 }}>
                            {loadingIA ? (
                                <ProgressoIA
                                    mensagens={MSGS_ANALISE}
                                    sub="A IA está lendo seu comando e analisando o cardápio. Isso pode levar alguns segundos."
                                />
                            ) : transcrevendo ? (
                                <ProgressoIA
                                    mensagens={["Transcrevendo o áudio…"]}
                                    sub="Convertendo sua fala em texto. Você poderá revisar antes de enviar."
                                />
                            ) : (
                                <>
                                    <Typography className={classes.hint}>
                                        Descreva em linguagem natural o que deseja alterar no cardápio.
                                    </Typography>
                                    {cardapioGrande && (
                                        <div className={classes.avisoCardapioGrande}>
                                            <span>💡</span>
                                            <span>
                                                Seu cardápio é grande ({totalProdutos} produtos).{" "}
                                                <strong>Cite a categoria no comando</strong> — ex.: "Na categoria
                                                Esfihas, ..." — para um resultado melhor e mais rápido.
                                            </span>
                                        </div>
                                    )}
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        variant="outlined"
                                        label="O que deseja fazer?"
                                        placeholder='Ex: "Inative todos os itens da categoria Pizzas"'
                                        value={texto}
                                        onChange={e => setTexto(e.target.value)}
                                        disabled={loadingIA}
                                        autoFocus
                                        onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) handleEnviar() }}
                                    />
                                    <div className={classes.micRow}>
                                        {!gravando ? (
                                            <Button
                                                className={classes.botaoFalar}
                                                onClick={iniciarGravacao}
                                                startIcon={<span aria-hidden>🎤</span>}
                                            >
                                                Falar o comando
                                            </Button>
                                        ) : (
                                            <>
                                                <Button
                                                    className={classes.botaoParar}
                                                    onClick={pararGravacao}
                                                    startIcon={<span aria-hidden>⏹</span>}
                                                >
                                                    Parar gravação
                                                </Button>
                                                <span className={classes.pontoGravando} />
                                                <Typography style={{ fontSize: 12, color: "#dc2626" }}>
                                                    Gravando… fale o comando e clique em parar
                                                </Typography>
                                            </>
                                        )}
                                        <Typography
                                            style={{
                                                fontSize: 11,
                                                color: "#9ca3af",
                                                marginLeft: "auto",
                                            }}
                                        >
                                            Ctrl + Enter para enviar
                                        </Typography>
                                    </div>
                                </>
                            )}
                        </DialogContent>
                        {!loadingIA && !transcrevendo && (
                            <DialogActions style={{ padding: "12px 20px" }}>
                                <Button onClick={handleFechar}>
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleEnviar}
                                    disabled={!texto.trim() || gravando}
                                    style={{
                                        background: !texto.trim() || gravando ? undefined : "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
                                        color: !texto.trim() || gravando ? undefined : "white",
                                        minWidth: 130,
                                    }}
                                >
                                    Enviar ✦
                                </Button>
                            </DialogActions>
                        )}
                    </>
                ) : (
                    <>
                        <DialogTitle className={classes.dialogTitle}>
                            {salvando
                                ? "Aplicando as alterações…"
                                : "Confira as alterações antes de aplicar"}
                        </DialogTitle>
                        <DialogContent style={{ paddingTop: 20 }}>
                            {salvando ? (
                                <ProgressoIA
                                    mensagens={MSGS_APLICACAO}
                                    sub="Salvando as mudanças no cardápio. Não feche esta janela."
                                />
                            ) : (
                                <>
                            <span className={classes.badge}>
                                {resultado.alteracoes?.length || 0}{" "}
                                {(resultado.alteracoes?.length || 0) === 1
                                    ? "alteração"
                                    : "alterações"}{" "}
                                a aplicar
                            </span>

                            <div className={classes.resumoBox}>
                                <Typography variant="body2">{resultado.resumo}</Typography>
                            </div>

                            <Typography variant="body2" style={{ color: "#6b7280", marginBottom: 8 }}>
                                Nada foi alterado ainda. Revise abaixo e clique em{" "}
                                <strong>Aplicar alterações</strong> para confirmar.
                            </Typography>

                            {resultado.alteracoes?.length > 0 && (
                                <>
                                    <Typography variant="caption" style={{ color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                        Itens que serão alterados
                                    </Typography>
                                    <div className={classes.listaAlteracoes}>
                                        {resultado.alteracoes.map((alt, i) => {
                                            const cor = TIPO_CORES[alt.tipo]
                                            return (
                                                <div key={i} className={classes.itemAlteracao}>
                                                    <div>
                                                        <span
                                                            className={classes.tipoChip}
                                                            style={{ backgroundColor: cor.bg, color: cor.color }}
                                                        >
                                                            {cor.label}
                                                        </span>
                                                        <span className={classes.nomeItem}>{alt.nome}</span>
                                                    </div>
                                                    {alt.breadcrumb && (
                                                        <span className={classes.breadcrumb}>{alt.breadcrumb}</span>
                                                    )}
                                                    <span className={classes.acaoItem}>
                                                        {alt.acoes.join(" · ")}
                                                    </span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </>
                            )}
                                </>
                            )}
                        </DialogContent>
                        {!salvando && (
                            <DialogActions style={{ padding: "12px 20px" }}>
                                <Button
                                    variant="outlined"
                                    className={classes.botaoDescartar}
                                    onClick={handleDescartar}
                                >
                                    Descartar
                                </Button>
                                <Button
                                    variant="contained"
                                    className={classes.botaoSalvar}
                                    onClick={handleSalvarAgora}
                                >
                                    Aplicar alterações
                                </Button>
                            </DialogActions>
                        )}
                    </>
                )}
            </Dialog>

            {/* Diálogo guiado quando o microfone está bloqueado. O navegador não permite
                reabrir o popup nativo depois de negado, então orientamos o passo a passo. */}
            <Dialog open={dialogPermissao} onClose={() => setDialogPermissao(false)} maxWidth="xs" fullWidth>
                <DialogTitle className={classes.dialogTitle}>Permitir o microfone</DialogTitle>
                <DialogContent style={{ paddingTop: 20 }}>
                    <Typography variant="body2" style={{ marginBottom: 12 }}>
                        O acesso ao microfone está <strong>bloqueado</strong> para este site. Por
                        segurança, o navegador não deixa a página reabrir a permissão — você
                        precisa liberá-la manualmente:
                    </Typography>
                    <ol style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.7 }}>
                        <li>
                            Clique no ícone à <strong>esquerda do endereço</strong> (cadeado /
                            controles do site 🎚️).
                        </li>
                        <li>
                            Em <strong>Microfone</strong>, mude para <strong>Permitir</strong>.
                        </li>
                        <li>
                            <strong>Recarregue</strong> a página e clique em “Falar o comando”
                            novamente.
                        </li>
                    </ol>
                    <Typography variant="caption" style={{ display: "block", marginTop: 12, color: "#6b7280" }}>
                        No Windows, confira também em Configurações → Privacidade → Microfone se o
                        acesso está ligado para o navegador.
                    </Typography>
                </DialogContent>
                <DialogActions style={{ padding: "12px 20px" }}>
                    <Button onClick={() => setDialogPermissao(false)}>Fechar</Button>
                    <Button
                        variant="contained"
                        onClick={() => window.location.reload()}
                        style={{
                            background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
                            color: "white",
                        }}
                    >
                        Recarregar página
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
